import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Divider,
  Switch,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Groups, VisibilityOff, PersonAddAlt1 } from '@mui/icons-material';
import { BellIcon, BellSimpleIcon, GearIcon, DotsThreeIcon, FlameIcon, UsersIcon, UserIcon } from '@phosphor-icons/react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import CustomTabs from '../widgets/CustomTabs';
import { User } from 'firebase/auth';
import { useUserFollow } from '../../contexts/UserFollowContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { socialService } from '../../services/socialService';
import { fetchCrewPostsForFeed, toggleCrewPostLike, deleteCrewPost } from '../../services/crewPostService';
import { toggleBundleLike } from '../../services/socialBundleService';
import SocialPostDialog from '../dialogs/SocialPostDialog';
import LikedByBottomSheet from '../dialogs/LikedByBottomSheet';
import {
  reviewToSocialPost,
  customPostToSocialPost,
  watchlistToSocialPost,
  bundleToSocialPost,
} from '../../utils/socialPostBuilders';
import type { SocialPostData } from '../../utils/socialPostBuilders';
import { bundleIdFromFeedItem } from '../../utils/crewFeedUtils';
import { unifiedGetDoc, unifiedDeleteDoc, CommentsService, getAppConfig, type CrewPostLinks } from '@watchlist/shared';
import { Capacitor } from '@capacitor/core';
import PullToRefresh from '../common/PullToRefresh';
import { useScrollContainer, getScrollTop, scrollSourceTo } from '../layout/ScrollContainer';
import StoriesRail from '../widgets/StoriesRail';
import HomeCardStack from '../widgets/HomeCardStack';
import { HelpOutline } from '@mui/icons-material';
import OpeningCreditsSheet from '../dialogs/OpeningCreditsSheet';
import { Logo } from '../widgets/Logo';
import ResponsiveContainer from '../layouts/ResponsiveContainer';
import CrewFeedItemComponent, { CrewFeedItemSkeleton } from '../widgets/CrewFeedItem';
import SuggestedUsersCarousel from 'components/widgets/SuggestedUsersCarousel';
import {
  bundleCrewItems,
  crewPostDocToFeedItem,
  mergeCrewFeedItems,
  sortCrewPostDocsForFeed,
  filterFeedItemsForAllTab,
  WATCHLIST_CREW_UID,
  CrewFeedItem,
} from '../../utils/crewFeedUtils';
import { font } from '../../utils/fontHelper';
import {
  loadDismissedSuggestionUids,
  persistDismissedSuggestionUids,
} from '../../utils/crewSuggestionDismiss';
import { useToast } from '../utils/ToastProvider';
import type { UserProfile, UserActivity } from '@watchlist/shared';
import { glassStyles } from '../../tokens/designTokens';
import { useMovieQuickView } from '../../contexts/MovieQuickViewContext';
import { useSwipeTab } from '../../hooks/useSwipeTab';

const CREW_SEEN_KEY = 'crew_last_opened_at';
const CREW_SCROLL_KEY = 'crew_scroll_position';
const CREW_HIDE_FIND_KEY = 'crew_hide_find_suggestions';
const FOLLOWING_CHUNK = 10;   // Firestore 'in' operator limit
const PER_CHUNK_LIMIT = 50;   // activities per chunk
const PAGE_SIZE = 20;         // bundled feed items shown at once

const LoadMoreSentinel: React.FC<{ onVisible: () => void }> = ({ onVisible }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  // Keep a stable ref to the callback so the effect never needs to re-run
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        observer.disconnect();
        onVisibleRef.current();
      }
    }, { rootMargin: '100px' });
    // Delay observation so the sentinel doesn't fire during initial render
    // before the page has finished laying out content.
    const timer = setTimeout(() => observer.observe(el), 500);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);
  return <div ref={ref} style={{ height: 1 }} />;
};
const FEED_CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

// Extracts a JS Date from a createdAt field regardless of whether it's an ISO string,
// a Firestore Timestamp object, or a plain {seconds} shape from the native adapter.
function createdAtToDate(value: any): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
  return undefined;
}
const GLOBAL_FEED_LIMIT = 100; // raw activities pulled for the "All posts" tab (paginated via scroll)

// Tab indices — keep in sync with the tabs definition in render()
const TAB_ALL = 0;
const TAB_FOLLOWING = 1;
const TAB_YOU = 2;

// ── Module-level types (must live here so the caches below can reference them) ──
type Engagement = { likeCount: number; likedBy: string[]; commentCount: number };

function getEngagementScore(item: CrewFeedItem, nowMs: number, engagement: Map<string, Engagement>): number {
  const cfg = getAppConfig();
  const eng = engagement.get(bundleIdFromFeedItem(item) ?? '');
  const likes = eng?.likeCount ?? 0;
  const comments = eng?.commentCount ?? 0;
  const ageMs = Math.max(0, nowMs - item.createdAt);
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  const decayFactor = Math.pow(0.5, ageDays / cfg.decayHalfLifeDays);
  const typePriority = item.kind === 'crew_post' ? 3
    : item.activities.some(a => a.type === 'movie_reviewed' || a.metadata?.review) ? 2
    : 1;
  const newness = Math.max(0, cfg.newnessBonusPoints * (1 - ageDays / cfg.newnessWindowDays));
  return (likes * 2 + comments * 3 + typePriority + newness) * decayFactor;
}

/**
 * Sort by engagement score, then promote up to cfg.reservedSlots of the newest non-crew-post
 * activity items (younger than cfg.reservedMaxAgeHours) to the front so users always see fresh content.
 * Pinned crew posts are left untouched at the front.
 * If fewer than reservedSlots qualify, only those that do are promoted.
 */
function applyFeedOrder(items: CrewFeedItem[], nowMs: number, engagement: Map<string, Engagement>): CrewFeedItem[] {
  const cfg = getAppConfig();
  const sorted = [...items].sort(
    (a, b) => getEngagementScore(b, nowMs, engagement) - getEngagementScore(a, nowMs, engagement)
  );

  // Separate pinned crew posts — they stay at the front unconditionally
  const pinned = sorted.filter(i => i.kind === 'crew_post' && (i.crewPost?.pinExpiresAtMs ?? 0) > nowMs);
  const rest = sorted.filter(i => !(i.kind === 'crew_post' && (i.crewPost?.pinExpiresAtMs ?? 0) > nowMs));

  // Promote the newest activity posts within the configured age window
  const cutoff = nowMs - cfg.reservedMaxAgeHours * 60 * 60 * 1000;
  const candidates = rest
    .filter(i => i.kind !== 'crew_post' && i.createdAt >= cutoff)
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, cfg.reservedSlots);

  if (candidates.length === 0) return [...pinned, ...rest];

  const reservedIds = new Set(candidates.map(i => i.id));
  const remainder = rest.filter(i => !reservedIds.has(i.id));
  return [...pinned, ...candidates, ...remainder];
}
type WatchlistMeta = Engagement & {
  name: string;
  description?: string;
  moviePosters?: string[];
  movieCount?: number;
  authorUid: string;
};

// Module-level caches — survive navigation unmounts so switching tabs causes no blink
let _cachedFeedItems: CrewFeedItem[] | null = null;
let _feedCacheTimestamp = 0;
let _cachedBundleEngagement = new Map<string, Engagement>();
let _cachedWatchlistEngagement = new Map<string, WatchlistMeta>();
let _cachedReviewEngagement = new Map<string, Engagement>();
let _cachedSuggestedUsers: UserProfile[] | null = null;
let _cachedActiveTab: number = TAB_ALL;
let _cachedYouItems: CrewFeedItem[] | null = null;
const _tabScrollPositions: Record<number, number> = {};
// True once any tab has had tabsInAppBar=true (tabs scrolled into AppBar).
let _anyTabHadTabsInAppBar = false;
// scrollTop + inline-tabs-row height captured the first time the inline tabs leave
// the viewport. Restoring to this position puts the first post at the top of the
// visible area with the AppBar (showing tabs) covering nothing above it.
let _tabsInAppBarScrollThreshold = 0;
// IDs explicitly deleted this session via the crewPostDeleted / crewBundleDeleted
// window events (admin/self delete from the 3-dot menu). Persisted at module scope
// so items stay gone across tab switches when CrewPage remounts from cache.
// Do NOT populate these from inferred signals like "socialBundles doc is missing" —
// bundle docs are created lazily on first like, so absence is normal, not a deletion.
const _deletedBundleIds = new Set<string>();
const _deletedCrewPostIds = new Set<string>();
// Review IDs in "{uid}_{movieId}" format (matches userMovies doc ID)
const _deletedReviewIds = new Set<string>();

export function markCrewSeen() {
  localStorage.setItem(CREW_SEEN_KEY, Date.now().toString());
}

export function getLastCrewOpenedAt(): number {
  const v = localStorage.getItem(CREW_SEEN_KEY);
  return v ? parseInt(v, 10) : 0;
}

interface CrewPageProps {
  user?: User | null;
  onMovieSelect?: (movieId: string) => void;
  onUserSelect?: (userId: string) => void;
  onWatchlistSelect?: (watchlistId: string) => void;
  onReviewSelect?: (reviewId: string) => void;
  onPersonSelect?: (personId: string) => void;
}

const CrewPage: React.FC<CrewPageProps> = ({
  user,
  onUserSelect,
  onWatchlistSelect,
  onReviewSelect,
  onPersonSelect,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openMovieQuickView } = useMovieQuickView();
  const [searchParams] = useSearchParams();
const isDark = theme.palette.mode === 'dark';
  const hasRestoredScroll = useRef(false);
  const highlightPostId = searchParams.get('postId');
  const highlightBundleId = searchParams.get('bundleId');
  const openCommentsOnArrival = searchParams.get('openComments') === 'true';
  const highlightCommentId = searchParams.get('highlightCommentId');
  const highlightScrollDoneRef = useRef(false);
  const openCommentsDoneRef = useRef(false);
  const bundleScrollDoneRef = useRef(false);

  const { followStats, isLoading: followLoading, followUser, unfollowUser, isUserFollowed } = useUserFollow();
  const { getUserDisplayName, getUserAvatar, preloadProfiles, getUserProfile } = useUserProfileCache();
  const { showSuccess, showError } = useToast();
  const { canModerateCrew } = useAuth();

  const [dismissedSuggestionUids, setDismissedSuggestionUids] = useState<Set<string>>(() =>
    loadDismissedSuggestionUids()
  );
  const [feedItems, setFeedItems] = useState<CrewFeedItem[]>(_cachedFeedItems ?? []);
  const [youItems, setYouItems] = useState<CrewFeedItem[]>(_cachedYouItems ?? []);
  const [testerUidSet, setTesterUidSet] = useState<Set<string>>(new Set());

  // Paginated You-tab state
  const [youActivities, setYouActivities] = useState<UserActivity[]>([]);
  const [youLastDate, setYouLastDate] = useState<Date | undefined>();
  const [youHasMore, setYouHasMore] = useState(false);
  const [youLoadingMore, setYouLoadingMore] = useState(false);
  // True while the You-tab activity fetch is in progress (initial load only).
  const [isYouLoading, setIsYouLoading] = useState(_cachedYouItems === null && !!user?.uid);

  // Paginated Following-tab state
  const [followingActivities, setFollowingActivities] = useState<UserActivity[]>([]);
  const [followingLastCreatedAt, setFollowingLastCreatedAt] = useState<Date | undefined>();
  const [followingHasMore, setFollowingHasMore] = useState(false);
  const [followingLoadingMore, setFollowingLoadingMore] = useState(false);

  // Paginated All-tab state (global activity cursor)
  const [allLastDocId, setAllLastDocId] = useState<string | undefined>();
  const [allHasMore, setAllHasMore] = useState(false);
  const [allLoadingMore, setAllLoadingMore] = useState(false);
  // Raw global activities accumulated across pages — used to rebuild feedItems on each page
  const allActivitiesRef = useRef<UserActivity[]>([]);
  // True while the first batch is shown but the full feed hasn't loaded yet.
  const [isFullFeedLoading, setIsFullFeedLoading] = useState(false);
  const [isFeedLoading, setIsFeedLoading] = useState(_cachedFeedItems === null);
  const [error, setError] = useState<string | null>(null);
  const [suggestedUsers, setSuggestedUsers] = useState<UserProfile[]>(_cachedSuggestedUsers ?? []);

  const [openingCreditsOpen, setOpeningCreditsOpen] = useState(false);

  // AppBar menu state
  const [appBarMenuAnchor, setAppBarMenuAnchor] = useState<HTMLElement | null>(null);
  const appBarMenuOpen = Boolean(appBarMenuAnchor);
  // Tabs: 0 = All posts (global), 1 = Following, 2 = You
  const [activeTab, setActiveTab] = useState<number>(_cachedActiveTab);
  const [visibleCounts, setVisibleCounts] = useState<Record<number, number>>({
    [TAB_ALL]: PAGE_SIZE,
    [TAB_FOLLOWING]: PAGE_SIZE * 5,
    [TAB_YOU]: PAGE_SIZE * 5,
  });
  const visibleCount = visibleCounts[activeTab] ?? PAGE_SIZE;
  const setVisibleCount = useCallback((updater: number | ((v: number) => number)) => {
    setVisibleCounts(prev => ({
      ...prev,
      [activeTab]: typeof updater === 'function' ? updater(prev[activeTab] ?? PAGE_SIZE) : updater,
    }));
  }, [activeTab]);
  const [isSwitchingTab, setIsSwitchingTab] = useState(false);
  const isSwitchingTabRef = useRef(false);
  const switchTabTimerRef = useRef<number | null>(null);
  // Per-tab saved scroll positions — backed by module-level cache so they survive navigation
  const tabScrollPositions = useRef<Record<number, number>>(_tabScrollPositions);
  // Stable ref so handleTabChange (defined before scrollSource) can read it.
  const scrollSourceRef = useRef<HTMLElement | null>(null);
  // Stable ref so handleTabChange can read/write tabsInAppBar without closing over stale state.
  const tabsInAppBarRef = useRef(false);
  const setTabsInAppBarRef = useRef((_: boolean) => {});

  const handleTabReselect = useCallback(() => {
    scrollSourceTo(scrollSourceRef.current, { top: 0, behavior: 'smooth' });
  }, []);

  const handleTabChange = useCallback((v: number) => {
    setActiveTab(prev => {
      if (prev === v) return prev;
      _cachedActiveTab = v;
      // Capture scroll position NOW before isSwitchingTab triggers a skeleton
      // render that shrinks content and lets the browser clamp scrollTop to 0.
      const pos = Math.round(getScrollTop(scrollSourceRef.current));
      tabScrollPositions.current[prev] = pos;
      _tabScrollPositions[prev] = pos;
      // Only set flag if threshold was actually reached by scrolling (threshold>0
      // means the IO fired while scrolled, not on mount at scrollTop=0 on iOS).
      if (tabsInAppBarRef.current && _tabsInAppBarScrollThreshold > 0) _anyTabHadTabsInAppBar = true;
      isSwitchingTabRef.current = true;
      setIsSwitchingTab(true);
      if (switchTabTimerRef.current) window.clearTimeout(switchTabTimerRef.current);
      switchTabTimerRef.current = window.setTimeout(() => {
        // isSwitchingTabRef stays true until restore effect re-sets it after scroll.
        setIsSwitchingTab(false);
        switchTabTimerRef.current = null;
      }, 300);
      return v;
    });
  }, []);
  useEffect(() => () => {
    if (switchTabTimerRef.current) window.clearTimeout(switchTabTimerRef.current);
  }, []);

  useSwipeTab(activeTab, 3, handleTabChange);

  // Sticky header style — matches HomePage shell behavior on scroll
  const scrollSource = useScrollContainer();
  scrollSourceRef.current = scrollSource;
  const [isScrolled, setIsScrolled] = useState(false);

  // Stable refs so scroll handler always sees latest pagination state without re-subscribing
  const allHasMoreRef = useRef(false);
  const allLoadingMoreRef = useRef(false);
  const followingHasMoreRef = useRef(false);
  const followingLoadingMoreRef = useRef(false);
  const youHasMoreRef = useRef(false);
  const youLoadingMoreRef = useRef(false);
  const activeTabRef = useRef(TAB_ALL);
  const loadMoreAllRef = useRef<() => void>(() => {});
  const loadMoreFollowingRef = useRef<() => void>(() => {});
  const loadMoreYouRef = useRef<() => void>(() => {});

  useEffect(() => {
    const target: EventTarget = scrollSource ?? window;
    const handleScroll = () => {
      setIsScrolled(getScrollTop(scrollSource) > 50);

      // Check if near bottom (within 300px) and trigger Firestore pagination
      const el = scrollSource as HTMLElement | null;
      const scrollTop = el ? el.scrollTop : window.scrollY;
      const scrollHeight = el ? el.scrollHeight : document.documentElement.scrollHeight;
      const clientHeight = el ? el.clientHeight : window.innerHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      const tab = activeTabRef.current;
      if (distanceFromBottom < 300) {
        if (tab === TAB_ALL && allHasMoreRef.current && !allLoadingMoreRef.current) {
          loadMoreAllRef.current();
        } else if (tab === TAB_FOLLOWING && followingHasMoreRef.current && !followingLoadingMoreRef.current) {
          loadMoreFollowingRef.current();
        } else if (tab === TAB_YOU && youHasMoreRef.current && !youLoadingMoreRef.current) {
          loadMoreYouRef.current();
        }
      }
    };
    target.addEventListener('scroll', handleScroll);
    return () => target.removeEventListener('scroll', handleScroll);
  }, [scrollSource]);

  // When the inline tabs scroll out of view, mirror them into the AppBar.
  const inlineTabsRef = useRef<HTMLDivElement | null>(null);
  const [tabsInAppBar, setTabsInAppBar] = useState(false);
  setTabsInAppBarRef.current = setTabsInAppBar;
  useEffect(() => {
    const el = inlineTabsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Ignore during tab switch — skeleton content is shorter and can falsely
        // bring the inline tabs back into view, causing two tab bars on screen.
        if (isSwitchingTabRef.current) return;
        const collapsed = !entry.isIntersecting;
        setTabsInAppBar(collapsed);
        tabsInAppBarRef.current = collapsed;
        const currentScrollTop = Math.round(getScrollTop(scrollSourceRef.current));
        if (collapsed && _tabsInAppBarScrollThreshold === 0 && currentScrollTop > 0) {
          // Only capture when actually scrolled — guards against iOS WKWebView
          // firing the IO on mount at scrollTop=0 before any user scroll.
          _tabsInAppBarScrollThreshold = currentScrollTop;
        }
      },
      { rootMargin: `${Capacitor.isNativePlatform() ? -132 : -76}px 0px 0px 0px`, threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Hide/show "Find crew" suggestions strip (persisted)
  const [hideFindCrew, setHideFindCrew] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CREW_HIDE_FIND_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleHideFindCrew = useCallback(() => {
    setHideFindCrew(prev => {
      const next = !prev;
      try { localStorage.setItem(CREW_HIDE_FIND_KEY, next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  }, []);

  const [storiesRefreshKey, setStoriesRefreshKey] = useState(0);

  // Notifications (for AppBar bell)
  const { unreadCount } = useNotifications();

  const visibleSuggestedUsers = useMemo(
    () => suggestedUsers.filter(u => !dismissedSuggestionUids.has(u.uid)),
    [suggestedUsers, dismissedSuggestionUids]
  );

  // Stable ref so loadFeed doesn't re-create when followingIds array changes identity
  const followingIdsRef = useRef<string[]>([]);
  followingIdsRef.current = followStats.followedUsers;

  // Crew post docs ref — kept so loadMoreAll can re-merge without re-fetching crew posts
  const crewPostDocsRef = useRef<{ id: string; data: Record<string, unknown> }[]>([]);
  // Full combined activity pool (following + own + global) for All-tab rebuild on pagination
  const allCombinedRef = useRef<UserActivity[]>([]);
  // True when loadFeed pre-sorted feedItems using cached engagement before the first render.
  // Tells the engagement sort effect to skip re-sorting (feedItems already in score order).
  const feedItemsPreSortedRef = useRef(false);

  /**
   * Verify which watchlist IDs still exist in Firestore AND have at least 2 movies.
   * Watchlists with 0–1 movies are excluded so the feed only surfaces meaningful lists.
   */
  const getLiveWatchlistIds = useCallback(async (ids: string[]): Promise<Set<string>> => {
    if (ids.length === 0) return new Set();
    const checks = await Promise.allSettled(ids.map(id => unifiedGetDoc('watchlists', id)));
    const live = new Set<string>();
    checks.forEach((result, i) => {
      if (result.status === 'fulfilled' && result.value?.exists?.()) {
        const data = result.value.data?.() ?? result.value;
        const movieCount = data?.movieCount ?? data?.movieDetails?.length ?? 0;
        if (movieCount >= 2) {
          live.add(ids[i]);
        }
      }
    });
    return live;
  }, []);

  const loadFeed = useCallback(async (force = false) => {
    const followingIds = followingIdsRef.current;
    const hasFreshCache = _cachedFeedItems !== null && Date.now() - _feedCacheTimestamp < FEED_CACHE_TTL_MS;
    if (!force && hasFreshCache) return;

    // Only show skeleton on the very first load (no cache yet)
    const isColdLoad = _cachedFeedItems === null;
    if (isColdLoad) setIsFeedLoading(true);
    setError(null);

    try {
      // On cold load: fetch crew posts + a small first batch of global activity in parallel
      // so we can show a sorted first page quickly without waiting for the full data set.
      const FIRST_BATCH_SIZE = 25;
      const [crewPostDocs, firstBatchRaw] = await Promise.all([
        fetchCrewPostsForFeed().catch(() => [] as { id: string; data: Record<string, unknown> }[]),
        isColdLoad && _cachedBundleEngagement.size === 0
          ? socialService.getAllPostsActivity(FIRST_BATCH_SIZE).catch(() => [] as UserActivity[])
          : Promise.resolve([] as UserActivity[]),
      ]);

      crewPostDocsRef.current = crewPostDocs;
      const crewLinkedUserIds: string[] = [];
      for (const { data } of crewPostDocs) {
        const links = (data.links || {}) as CrewPostLinks;
        links.userIds?.forEach(u => crewLinkedUserIds.push(u));
      }
      await preloadProfiles(
        Array.from(new Set([...followingIds, WATCHLIST_CREW_UID, ...crewLinkedUserIds, ...(user?.uid ? [user.uid] : [])]))
      ).catch(() => {});

      // Cold load with no cached engagement: render the first batch immediately so the
      // skeleton drops fast. Crew posts are excluded here — they will be added in the
      // full merge below to avoid old crew posts appearing at the top of an incomplete list.
      if (firstBatchRaw.length > 0) {
        const firstAuthorIds = Array.from(new Set(firstBatchRaw.map(a => a.uid)));
        await preloadProfiles(firstAuthorIds).catch(() => {});
        const firstBundled = bundleCrewItems(firstBatchRaw);
        // Don't set isFeedLoading false yet — engagement effect will do that after sorting.
        feedItemsPreSortedRef.current = false;
        setIsFullFeedLoading(true);
        setFeedItems(firstBundled);
        setVisibleCounts(prev => ({ ...prev, [TAB_ALL]: PAGE_SIZE }));
      }

      // Fetch first page of following activities (100, timestamp-cursor pagination)
      const followingPage = followingIds.length > 0
        ? await socialService.getFollowingActivityPage(followingIds, 200).catch(() => [] as UserActivity[])
        : [];
      setFollowingActivities(followingPage);
      const followingLastDate = followingPage.length > 0
        ? createdAtToDate(followingPage[followingPage.length - 1].createdAt)
        : undefined;
      setFollowingLastCreatedAt(followingLastDate);
      setFollowingHasMore(followingPage.length >= 200);

      // Fetch ALL of user's own activities upfront — loop pages until exhausted.
      // Done inline (no useEffect chaining) so the You tab is fully populated on load.
      let allYouActivities: UserActivity[] = [];
      if (user?.uid) {
        let cursor: Date | undefined;
        while (true) {
          const page = await socialService.getUserActivityPage(user.uid, 50, cursor).catch(() => [] as UserActivity[]);
          if (page.length === 0) break;
          const seen = new Set(allYouActivities.map(a => a.id));
          allYouActivities = [...allYouActivities, ...page.filter(a => !seen.has(a.id))];
          if (page.length < 50) break;
          cursor = createdAtToDate(page[page.length - 1].createdAt);
        }
      }
      setYouActivities(allYouActivities);
      setYouLastDate(undefined);
      setYouHasMore(false);

      // Build You-tab bundle (includes hidden activities for hiddenPostCount)
      const youBundled = bundleCrewItems(allYouActivities, true);
      _cachedYouItems = youBundled;
      setYouItems(youBundled);
      setIsYouLoading(false);
      // Use allYouActivities as userActivities for the All-tab merge below
      const userActivities = allYouActivities;

      // Also keep a small set of following activities available for the All-tab merge
      const results = await Promise.allSettled(
        ((): string[][] => {
          const chunks: string[][] = [];
          for (let i = 0; i < followingIds.length; i += FOLLOWING_CHUNK) {
            chunks.push(followingIds.slice(i, i + FOLLOWING_CHUNK));
          }
          return chunks;
        })().map(chunk => socialService.getFollowingActivity(chunk, PER_CHUNK_LIMIT))
      );

      // Fetch the full global activity batch. If we already fetched a first batch on cold
      // load, fetch the remainder so we have the full GLOBAL_FEED_LIMIT for the cache.
      const alreadyFetched = firstBatchRaw.length;
      const globalActivitiesRaw = alreadyFetched >= GLOBAL_FEED_LIMIT
        ? firstBatchRaw
        : await (async () => {
          if (alreadyFetched > 0) {
            // Fetch remainder starting after the last doc of the first batch
            const lastId = firstBatchRaw[firstBatchRaw.length - 1]?.id;
            const rest = await socialService
              .getAllPostsActivityPage(GLOBAL_FEED_LIMIT - alreadyFetched, lastId)
              .catch((err: unknown) => { console.warn('CrewPage: global activity fetch failed', err); return []; });
            return [...firstBatchRaw, ...rest];
          }
          return socialService
            .getAllPostsActivity(GLOBAL_FEED_LIMIT)
            .catch((err: unknown) => { console.warn('CrewPage: global activity fetch failed', err); return []; });
        })();

      // Store raw global activities for All-tab pagination
      allActivitiesRef.current = globalActivitiesRaw;
      const globalLastDocId = globalActivitiesRaw.length > 0
        ? globalActivitiesRaw[globalActivitiesRaw.length - 1].id
        : undefined;
      setAllLastDocId(globalLastDocId);
      setAllHasMore(globalActivitiesRaw.length >= GLOBAL_FEED_LIMIT);

      // Combine following + own + global activities, deduped by activity id.
      const byId = new Map<string, typeof userActivities[number]>();
      for (const a of results.flatMap(r => (r.status === 'fulfilled' ? r.value : []))) byId.set(a.id, a);
      for (const a of userActivities) byId.set(a.id, a);
      for (const a of globalActivitiesRaw) if (!byId.has(a.id)) byId.set(a.id, a);
      const all = Array.from(byId.values());
      allCombinedRef.current = all;

      // Preload profiles for any new authors surfaced by the global fetch.
      const globalAuthorIds = Array.from(new Set(globalActivitiesRaw.map(a => a.uid)));
      if (globalAuthorIds.length > 0) {
        await preloadProfiles(globalAuthorIds).catch(() => {});
      }

      const watchlistActivityIds = Array.from(new Set(
        all
          .filter(a => ['watchlist_created', 'created_watchlist'].includes(a.type))
          .map(a => a.watchlistId || a.listId)
          .filter((id): id is string => !!id)
      ));

      const bundledUnfiltered = bundleCrewItems(all);
      const nowMs = Date.now();
      const sortedCrewDocs = sortCrewPostDocsForFeed(crewPostDocs, nowMs);
      const crewItems = sortedCrewDocs.map(d => crewPostDocToFeedItem(d.id, d.data, nowMs));
      const merged = mergeCrewFeedItems(bundledUnfiltered, crewItems, nowMs);

      // Ensure ALL actor profiles in the merged feed are cached before we set
      // feed items. This prevents private-profile content from flashing on the
      // first render of the All tab before profiles load.
      const allActorIds = Array.from(new Set(merged.map(i => i.actorId).filter(Boolean)));
      await preloadProfiles(allActorIds).catch(() => {});

      _cachedFeedItems = merged;
      _feedCacheTimestamp = Date.now();

      // Build tester set from profiles that are now fully loaded in the cache.
      const testers = new Set<string>();
      for (const uid of allActorIds) {
        if (getUserProfile(uid)?.role === 'test') testers.add(uid);
      }
      setTesterUidSet(testers);

      // Warm load: engagement cache already populated — pre-sort so first render is final order.
      // Cold load: engagement cache empty — hold skeleton; engagement effect sorts + clears it.
      feedItemsPreSortedRef.current = false;
      let initialFeed = merged;
      if (_cachedBundleEngagement.size > 0) {
        const sortNowMs = Date.now();
        initialFeed = applyFeedOrder(merged, sortNowMs, _cachedBundleEngagement);
        feedItemsPreSortedRef.current = true;
      }

      setFeedItems(initialFeed);
      setIsFullFeedLoading(false);
      setVisibleCounts(prev => ({ ...prev, [TAB_ALL]: PAGE_SIZE }));
      // Clear skeleton now only if pre-sorted; cold-load skeleton clears in engagement effect.
      if (feedItemsPreSortedRef.current) setIsFeedLoading(false);

      if (watchlistActivityIds.length > 0) {
        void (async () => {
          try {
            const liveWatchlistIds = await getLiveWatchlistIds(watchlistActivityIds);
            const filtered = all.filter(a => {
              if (!['watchlist_created', 'created_watchlist'].includes(a.type)) return true;
              const wid = a.watchlistId || a.listId;
              return wid ? liveWatchlistIds.has(wid) : false;
            });
            const bundled = bundleCrewItems(filtered);
            const crewItemsRefined = sortedCrewDocs.map(d =>
              crewPostDocToFeedItem(d.id, d.data, nowMs)
            );
            const refined = mergeCrewFeedItems(bundled, crewItemsRefined, nowMs);
            _cachedFeedItems = refined;
            // Pre-sort refined feed using warm engagement cache if available
            if (_cachedBundleEngagement.size > 0) {
              const sortNowMs = Date.now();
              setFeedItems(applyFeedOrder(refined, sortNowMs, _cachedBundleEngagement));
            } else {
              setFeedItems(refined);
            }
          } catch (e) {
            console.warn('CrewPage: deferred watchlist validation failed', e);
          }
        })();
      }
    } catch (err) {
      console.error('CrewPage: failed to load feed', err);
      setError('Could not load Crew activity. Pull to refresh.');
      setIsFullFeedLoading(false);
      setIsYouLoading(false);
      setIsFeedLoading(false); // Always clear skeleton on error
    } finally {
      // Warm-load path already cleared isFeedLoading above.
      // Cold-load path: engagement effect clears it. Error path: cleared above.
      // This finally is a safety net for any path that didn't already clear it.
    }
  }, [preloadProfiles, getLiveWatchlistIds, getUserProfile]);

  /**
   * Load suggested users for discovery
   */
  // Use followingIdsRef (updated every render) when filtering so this callback stays stable when
  // follow lists change. Otherwise deps on followStats.followedUsers recreates loadSuggestions,
  // retriggers the effect below, and reloads the entire feed after every follow.
  const loadSuggestions = useCallback(async () => {
    if (!user?.uid) return;

    try {
      console.log('🔍 CrewPage: Loading suggested users');
      const suggestions = await socialService.getSuggestedUsers(user.uid, 20);

      const followed = followingIdsRef.current;
      const filtered = suggestions.filter(suggestedUser => !followed.includes(suggestedUser.uid));

      const limited = filtered.slice(0, 10);
      _cachedSuggestedUsers = limited;
      setSuggestedUsers(limited);

      if (limited.length > 0) {
        await preloadProfiles(limited.map(u => u.uid));
      }

      console.log(`✅ CrewPage: Loaded ${limited.length} suggested users`);
    } catch (err) {
      console.error('CrewPage: Failed to load suggested users', err);
    }
  }, [user?.uid, preloadProfiles]);

  const loadMoreYou = useCallback(async () => {
    if (!user?.uid || youLoadingMoreRef.current || !youHasMoreRef.current) return;
    setYouLoadingMore(true);
    try {
      const page = await socialService.getUserActivityPage(user.uid, 50, youLastDate);
      if (page.length === 0) { setYouHasMore(false); return; }
      setYouActivities(prev => {
        const existingIds = new Set(prev.map(a => a.id));
        const deduped = page.filter(a => !existingIds.has(a.id));
        const next = [...prev, ...deduped];
        const youBundled = bundleCrewItems(next, true);
        _cachedYouItems = youBundled;
        setYouItems(youBundled);
        setVisibleCounts(prev => ({ ...prev, [TAB_YOU]: Math.max(prev[TAB_YOU] ?? PAGE_SIZE, youBundled.length) }));
        return next;
      });
      setYouLastDate(createdAtToDate(page[page.length - 1].createdAt));
      setYouHasMore(page.length >= 50);
    } catch (err) {
      console.error('CrewPage: loadMoreYou failed', err);
    } finally {
      setYouLoadingMore(false);
    }
  }, [user?.uid, youLastDate]);

  const loadMoreFollowing = useCallback(async () => {
    const followingIds = followingIdsRef.current;
    if (followingIds.length === 0 || followingLoadingMoreRef.current || !followingHasMoreRef.current) return;
    setFollowingLoadingMore(true);
    try {
      const page = await socialService.getFollowingActivityPage(followingIds, 100, followingLastCreatedAt);
      if (page.length === 0) { setFollowingHasMore(false); return; }
      setFollowingActivities(prev => {
        const existingIds = new Set(prev.map(a => a.id));
        const next = [...prev, ...page.filter(a => !existingIds.has(a.id))];
        setVisibleCounts(prev => ({ ...prev, [TAB_FOLLOWING]: Math.max(prev[TAB_FOLLOWING] ?? PAGE_SIZE, bundleCrewItems(next).length) }));
        return next;
      });
      setFollowingLastCreatedAt(createdAtToDate(page[page.length - 1].createdAt));
      setFollowingHasMore(page.length >= 200);
    } catch (err) {
      console.error('CrewPage: loadMoreFollowing failed', err);
    } finally {
      setFollowingLoadingMore(false);
    }
  }, [followingLastCreatedAt]);

  const loadMoreAll = useCallback(async () => {
    if (allLoadingMoreRef.current || !allHasMoreRef.current) return;
    setAllLoadingMore(true);
    try {
      const page = await socialService.getAllPostsActivityPage(GLOBAL_FEED_LIMIT, allLastDocId);
      if (page.length === 0) { setAllHasMore(false); return; }

      // Dedup against full combined pool
      const existingIds = new Set(allCombinedRef.current.map(a => a.id));
      const newActivities = page.filter(a => !existingIds.has(a.id));
      allActivitiesRef.current = [...allActivitiesRef.current, ...newActivities];
      allCombinedRef.current = [...allCombinedRef.current, ...newActivities];

      // Preload profiles for new authors
      const newAuthorIds = Array.from(new Set(newActivities.map(a => a.uid)));
      if (newAuthorIds.length > 0) await preloadProfiles(newAuthorIds).catch(() => {});

      // Rebuild feedItems from full combined pool + crew posts
      const nowMs = Date.now();
      const sortedCrewDocs = sortCrewPostDocsForFeed(crewPostDocsRef.current, nowMs);
      const crewItems = sortedCrewDocs.map(d => crewPostDocToFeedItem(d.id, d.data, nowMs));
      const bundled = bundleCrewItems(allCombinedRef.current);
      const merged = mergeCrewFeedItems(bundled, crewItems, nowMs);
      _cachedFeedItems = merged;
      setFeedItems(merged);
      setVisibleCounts(prev => ({ ...prev, [TAB_ALL]: merged.length }));

      setAllLastDocId(page[page.length - 1].id);
      setAllHasMore(page.length >= GLOBAL_FEED_LIMIT);
    } catch (err) {
      console.error('CrewPage: loadMoreAll failed', err);
    } finally {
      setAllLoadingMore(false);
    }
  }, [allLastDocId, preloadProfiles]);

  // Wait until follow data is loaded before kicking off the feed fetch
  useEffect(() => {
    if (followLoading) return;
    markCrewSeen();
    loadFeed();
    loadSuggestions();
  }, [followLoading, loadFeed, loadSuggestions]);

  // Keep scroll-handler refs in sync with latest state/callbacks
  allHasMoreRef.current = allHasMore;
  allLoadingMoreRef.current = allLoadingMore;
  followingHasMoreRef.current = followingHasMore;
  followingLoadingMoreRef.current = followingLoadingMore;
  youHasMoreRef.current = youHasMore;
  youLoadingMoreRef.current = youLoadingMore;
  activeTabRef.current = activeTab;
  loadMoreAllRef.current = loadMoreAll;
  loadMoreFollowingRef.current = loadMoreFollowing;
  loadMoreYouRef.current = loadMoreYou;

  // ── Scroll-position persistence ──────────────────────────────────────────
  // Tab switching is handled natively by TabContainer (overflow:auto preserves
  // scrollTop across display:none). This save/restore only runs on SPA navigations
  // (back/forward within the app), NOT on hard page reloads.
  useEffect(() => {
    // Clear saved scroll on hard reload so page always starts at top
    if (performance.navigation?.type === 1 || performance.getEntriesByType?.('navigation').some((e: any) => e.type === 'reload')) {
      sessionStorage.removeItem(CREW_SCROLL_KEY);
    }
  }, []);

  useEffect(() => {
    return () => {
      sessionStorage.setItem(CREW_SCROLL_KEY, String(Math.round(getScrollTop(scrollSource))));
    };
  }, [scrollSource]);

  useEffect(() => {
    if (hasRestoredScroll.current || feedItems.length === 0) return;
    const saved = sessionStorage.getItem(CREW_SCROLL_KEY);
    if (!saved) return;
    hasRestoredScroll.current = true;
    requestAnimationFrame(() => {
      scrollSourceTo(scrollSource, { top: parseInt(saved, 10), behavior: 'instant' });
    });
  }, [feedItems, scrollSource]);
  // ─────────────────────────────────────────────────────────────────────────

  // Per-tab scroll save/restore.
  const prevTabRef = useRef(activeTab);
  const didSwitchTabRef = useRef(false);

  useEffect(() => {
    const prevTab = prevTabRef.current;
    if (prevTab === activeTab) return;
    prevTabRef.current = activeTab;
    didSwitchTabRef.current = true;
  }, [activeTab]);

  useEffect(() => {
    // Only restore after an actual tab switch, not on initial mount
    if (isSwitchingTab || !didSwitchTabRef.current) return;
    didSwitchTabRef.current = false;
    const saved = tabScrollPositions.current[activeTab] ?? 0;
    // If any tab has been scrolled past the inline-tabs threshold this session,
    // enforce that minimum so the AppBar always shows with tabs visible in it.
    const minScroll = _anyTabHadTabsInAppBar ? _tabsInAppBarScrollThreshold : 0;
    const target = Math.max(saved, minScroll);
    setIsScrolled(target > 50);
    // Eagerly set tabsInAppBar before content renders so tabs never flash inline.
    const willBeInAppBar = _tabsInAppBarScrollThreshold > 0 && target >= _tabsInAppBarScrollThreshold;
    tabsInAppBarRef.current = willBeInAppBar;
    setTabsInAppBarRef.current(willBeInAppBar);
    // Keep IO blocked until after scroll restore so WKWebView's delayed IO
    // callbacks don't override the eagerly-set tabsInAppBar state.
    isSwitchingTabRef.current = true;
    // Double rAF: first frame commits the layout, second frame applies scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollSourceTo(scrollSource, { top: target, behavior: 'instant' });
        // Unblock IO after scroll is applied so it re-evaluates at correct position.
        isSwitchingTabRef.current = false;
      });
    });
  }, [isSwitchingTab, activeTab, scrollSource]);

  useEffect(() => {
    if (activeTab !== TAB_FOLLOWING || !followingHasMore || followingLoadingMore) return;
    const el = scrollSource as HTMLElement | null;
    const scrollHeight = el ? el.scrollHeight : document.documentElement.scrollHeight;
    const clientHeight = el ? el.clientHeight : window.innerHeight;
    if (scrollHeight <= clientHeight * 2) {
      loadMoreFollowing();
    }
  }, [activeTab, followingHasMore, followingLoadingMore, followingActivities, scrollSource, loadMoreFollowing]);

  const handleRefresh = useCallback(async () => {
    markCrewSeen();
    sessionStorage.removeItem(CREW_SCROLL_KEY);
    hasRestoredScroll.current = false;
    // Clear per-tab scroll positions on refresh so all tabs start at top
    Object.keys(_tabScrollPositions).forEach(k => delete _tabScrollPositions[+k]);
    Object.keys(tabScrollPositions.current).forEach(k => delete tabScrollPositions.current[+k]);
    _anyTabHadTabsInAppBar = false;
    _tabsInAppBarScrollThreshold = 0;
    setIsScrolled(false);
    setVisibleCounts({
      [TAB_ALL]: PAGE_SIZE,
      [TAB_FOLLOWING]: PAGE_SIZE * 5,
      [TAB_YOU]: PAGE_SIZE * 5,
    });
    setYouActivities([]);
    setYouLastDate(undefined);
    setYouHasMore(false);
    setFollowingActivities([]);
    setFollowingLastCreatedAt(undefined);
    setFollowingHasMore(false);
    setStoriesRefreshKey(k => k + 1);
    // Clear module-level cache so loadFeed treats this as a cold load,
    // which triggers the skeleton and ensures fresh data replaces old content.
    _cachedFeedItems = null;
    _cachedYouItems = null;
    _feedCacheTimestamp = 0;
    setIsFeedLoading(true);
    setIsYouLoading(true);
    await loadFeed(true);
    await loadSuggestions();
  }, [loadFeed, loadSuggestions]);

  const handleFollowToggle = useCallback(async (userId: string) => {
    if (!user?.uid) return;

    const isFollowing = isUserFollowed(userId);
    if (isFollowing) {
      await unfollowUser(userId);
      await loadSuggestions();
    } else {
      await followUser(userId);
      setSuggestedUsers(prev => prev.filter(u => u.uid !== userId));
    }
  }, [user?.uid, isUserFollowed, followUser, unfollowUser, loadSuggestions]);

  const handleActorSelect = (userId: string) => {
    if (onUserSelect) onUserSelect(userId);
    else navigate(`/profile/${userId}`);
  };

  const handleMovieSelect = (movieId: string) => {
    openMovieQuickView(movieId);
  };

  const handleWatchlistSelect = (watchlistId: string) => {
    if (onWatchlistSelect) onWatchlistSelect(watchlistId);
    else navigate(`/watchlist/${watchlistId}`);
  };

  const handleReviewSelect = (reviewId: string) => {
    if (onReviewSelect) onReviewSelect(reviewId);
    else navigate(`/review/${encodeURIComponent(reviewId)}`);
  };

  const handlePersonSelect = (personId: string) => {
    if (onPersonSelect) onPersonSelect(personId);
    else navigate(`/person/${encodeURIComponent(personId)}`);
  };

  const handleDismissSuggestionCard = useCallback((userId: string) => {
    setDismissedSuggestionUids(prev => {
      const next = new Set(prev);
      next.add(userId);
      persistDismissedSuggestionUids(next);
      return next;
    });
  }, []);

  const handleFindCrewNavigate = useCallback(() => {
    navigate('/crew/find');
  }, [navigate]);

  // AppBar menu handlers
  const handleAppBarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAppBarMenuAnchor(event.currentTarget);
  };

  const handleAppBarMenuClose = () => {
    setAppBarMenuAnchor(null);
  };

  const handleHiddenFromCrewNavigate = () => {
    handleAppBarMenuClose();
    navigate('/crew/hidden');
  };

  // Hide activity from crew handler
  const handleHideFromCrew = useCallback(async (activityIds: string[]) => {
    if (!user?.uid) return;

    // Store the removed items for undo
    let removedItems: CrewFeedItem[] = [];

    try {
      // Hide all activities in the bundle
      await Promise.all(
        activityIds.map(id => socialService.hideActivityFromCrew(id, user.uid))
      );

      // Remove hidden items from the feed and store them
      setFeedItems(prev => {
        removedItems = prev.filter(item => {
          const itemActivityIds = item.activities.map(a => a.id);
          return activityIds.some(hiddenId => itemActivityIds.includes(hiddenId));
        });

        const next = prev.filter(item => {
          const itemActivityIds = item.activities.map(a => a.id);
          return !activityIds.some(hiddenId => itemActivityIds.includes(hiddenId));
        });

        // Keep module-level cache in sync so hidden posts don't reappear on tab return
        if (_cachedFeedItems) _cachedFeedItems = next;
        // Mark hidden in the combined activity pool so loadMoreAll won't resurface them
        const hiddenSet = new Set(activityIds);
        for (const a of allCombinedRef.current) {
          if (hiddenSet.has(a.id)) (a as any).hiddenFromCrew = true;
        }

        return next;
      });

      console.log(`✅ CrewPage: Hidden ${activityIds.length} activities from crew`);

      // Show success toast with undo option
      showSuccess(
        'Hidden from Crew',
        5000,
        {
          label: 'Undo',
          onClick: async () => {
            try {
              // Unhide all activities
              await Promise.all(
                activityIds.map(id => socialService.unhideActivityFromCrew(id, user.uid))
              );
              // Restore the items to the feed
              setFeedItems(prev => {
                const next = [...removedItems, ...prev];
                if (_cachedFeedItems) _cachedFeedItems = next;
                const unhiddenSet = new Set(activityIds);
                for (const a of allCombinedRef.current) {
                  if (unhiddenSet.has(a.id)) delete (a as any).hiddenFromCrew;
                }
                return next;
              });
              console.log(`✅ CrewPage: Unhid ${activityIds.length} activities (undo)`);
            } catch (undoError) {
              console.error('❌ CrewPage: Failed to undo hide:', undoError);
            }
          }
        }
      );
    } catch (error) {
      console.error('❌ CrewPage: Failed to hide activity from crew:', error);
    }
  }, [user?.uid, showSuccess]);

  // Delete crew post (own posts only)
  const [pendingDeletePostId, setPendingDeletePostId] = useState<string | null>(null);

  const handleDeleteCrewPost = useCallback((postDocId: string) => {
    setPendingDeletePostId(postDocId);
  }, []);

  const handleDeleteCrewPostConfirmed = useCallback(async () => {
    if (!pendingDeletePostId) return;
    const postDocId = pendingDeletePostId;
    setPendingDeletePostId(null);
    try {
      await deleteCrewPost(postDocId);
      _deletedCrewPostIds.add(postDocId);
      setFeedItems(prev => prev.filter(item => item.crewPost?.postDocId !== postDocId));
      showSuccess('Post deleted');
    } catch (err) {
      console.error('❌ CrewPage: Failed to delete crew post:', err);
      showError('Failed to delete post. Please try again.');
    }
  }, [pendingDeletePostId, showSuccess, showError]);

  const handleAdminDelete = useCallback(async (post: SocialPostData) => {
    try {
      if (post.kind === 'customPost') {
        await deleteCrewPost(post.id);
        _deletedCrewPostIds.add(post.id);
        setFeedItems(prev => prev.filter(item => item.crewPost?.postDocId !== post.id));
      } else if (post.kind === 'bundle') {
        await unifiedDeleteDoc('socialBundles', post.id);
        _deletedBundleIds.add(post.id);
        setFeedItems(prev => prev.filter(item => bundleIdFromFeedItem(item) !== post.id));
      } else if (post.kind === 'review') {
        await unifiedDeleteDoc('userMovies', post.id);
        _deletedReviewIds.add(post.id);
        // post.id is "{uid}_{movieId}" — delete the matching userActivity records so the
        // review doesn't reappear in the feed after the next refresh.
        const [uid, ...movieParts] = post.id.split('_');
        const movieId = movieParts.join('_');
        void socialService.deleteActivityByMovieId(uid, movieId);
        setFeedItems(prev => prev.filter(item => {
          const first = item.activities[0];
          const rid = (first as any)?.reviewId ?? (first?.uid && first?.movieId ? `${first.uid}_${first.movieId}` : '');
          return rid !== post.id;
        }));
      }
      showSuccess('Post deleted.');
    } catch (err) {
      console.error('❌ CrewPage: admin delete failed', err);
      showError('Failed to delete post.');
    }
  }, [showSuccess, showError]);

  // ── Unified social post dialog ───────────────────────────────────────────
  const [selectedPost, setSelectedPost] = useState<SocialPostData | null>(null);
  const [pendingHighlightCommentId, setPendingHighlightCommentId] = useState<string | null>(null);
  const [likedByUserIds, setLikedByUserIds] = useState<string[]>([]);

  // Engagement state — initialized from module-level caches so switching tabs causes no blink.
  // Synced back to module caches via effects below so the next mount starts warm.
  const [bundleEngagement, setBundleEngagement] = useState<Map<string, Engagement>>(_cachedBundleEngagement);
  const [watchlistEngagement, setWatchlistEngagement] = useState<Map<string, WatchlistMeta>>(_cachedWatchlistEngagement);
  const [reviewEngagement, setReviewEngagement] = useState<Map<string, Engagement>>(_cachedReviewEngagement);

  useEffect(() => { _cachedBundleEngagement = bundleEngagement; }, [bundleEngagement]);
  useEffect(() => { _cachedWatchlistEngagement = watchlistEngagement; }, [watchlistEngagement]);
  useEffect(() => { _cachedReviewEngagement = reviewEngagement; }, [reviewEngagement]);

  // Preload actor profiles for any items already in the module cache so names
  // are available immediately on mount (before loadFeed's async path completes).
  useEffect(() => {
    const cached = _cachedFeedItems;
    if (!cached || cached.length === 0) return;
    const ids = Array.from(new Set(cached.map(item => item.actorId)));
    preloadProfiles(ids).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback when the stored commentCount is 0/missing on the parent doc — the
  // Cloud Function may not have run, or the comments predate it. Counts the live
  // `comments` collection and patches the engagement map up to the actual count.
  // Only IDs whose stored value is 0 (or whose parent doc is missing) are queried.
  const backfillCommentCounts = useCallback(<T extends Engagement>(
    parentType: 'bundle' | 'watchlist' | 'review',
    ids: string[],
    docs: PromiseSettledResult<any>[],
    setMap: React.Dispatch<React.SetStateAction<Map<string, T>>>,
    isCancelled: () => boolean,
  ) => {
    // On native iOS/Android each listComments call polls the Firestore bridge — skipping
    // the backfill avoids firing dozens of bridge calls for every feed render.
    // The count stored on the parent doc is authoritative enough for the action bar.
    if (Capacitor.isNativePlatform()) return;

    const field = parentType === 'review' ? 'reviewCommentCount' : 'commentCount';
    const toCount = ids.filter((_, i) => {
      const res = docs[i];
      if (res.status !== 'fulfilled' || !res.value?.exists?.()) return true;
      const data: any = res.value.data?.() ?? res.value;
      return !data?.[field];
    });
    if (toCount.length === 0) return;
    (async () => {
      const results = await Promise.allSettled(
        toCount.map(id => CommentsService.listComments({ type: parentType, id }))
      );
      if (isCancelled()) return;
      setMap(prev => {
        const next = new Map(prev);
        results.forEach((res, i) => {
          if (res.status !== 'fulfilled') return;
          const id = toCount[i];
          const actual = res.value.length;
          const cur = next.get(id);
          if (cur && actual > cur.commentCount) {
            next.set(id, { ...cur, commentCount: actual });
          }
        });
        return next;
      });
    })();
  }, []);

  // Hydrate engagement data after feed items change. Batched parallel fetches.
  useEffect(() => {
    if (feedItems.length === 0) return;

    const bundleIds = new Set<string>();
    const watchlistIds = new Set<string>();
    const reviewIds = new Set<string>();
    const watchlistAuthor = new Map<string, string>(); // id -> actor uid

    for (const item of feedItems) {
      if (item.kind === 'movie_watched' || item.kind === 'movie_added') {
        const first = item.activities[0];
        const isReview = first?.type === 'movie_reviewed' || !!first?.metadata?.review;
        if (isReview) {
          const rid =
            (first as any)?.reviewId ||
            (first?.uid && first?.movieId ? `${first.uid}_${first.movieId}` : '');
          if (rid) reviewIds.add(rid);
        } else {
          const bid = bundleIdFromFeedItem(item);
          if (bid) bundleIds.add(bid);
        }
      } else if (item.kind === 'watchlist_created') {
        const first = item.activities[0] as any;
        const wid = first?.watchlistId || first?.entityId || first?.listId;
        if (wid) {
          watchlistIds.add(wid);
          watchlistAuthor.set(wid, item.actorId);
        }
      }
    }

    let cancelled = false;

    (async () => {
      if (bundleIds.size > 0) {
        const ids = Array.from(bundleIds);
        const docs = await Promise.allSettled(ids.map(id => unifiedGetDoc('socialBundles', id)));
        if (cancelled) return;

        // A missing socialBundles doc is the normal state for any bundle that
        // hasn't been liked yet — the doc is created lazily by likeSocialBundle
        // on first like. Treat missing docs as zero-engagement, not as deleted.
        // Explicit deletions come in through the crewBundleDeleted window event.
        setBundleEngagement(prev => {
          const next = new Map(prev);
          docs.forEach((res, i) => {
            const id = ids[i];
            if (res.status === 'fulfilled' && res.value?.exists?.()) {
              const data = res.value.data?.() ?? res.value;
              next.set(id, {
                likeCount: data?.likeCount ?? 0,
                likedBy: Array.isArray(data?.likedBy) ? data.likedBy : [],
                commentCount: data?.commentCount ?? 0,
              });
            } else if (!next.has(id)) {
              next.set(id, { likeCount: 0, likedBy: [], commentCount: 0 });
            }
          });
          return next;
        });
        // Some socialBundles docs have stale or missing commentCount (the Cloud
        // Function never ran, or comments were added before the counter existed).
        // Fall back to counting the live `comments` collection so the inline
        // action bar matches what the dialog shows.
        backfillCommentCounts('bundle', ids, docs, setBundleEngagement, () => cancelled);
      }

      if (watchlistIds.size > 0) {
        const ids = Array.from(watchlistIds);
        const [docs, likers] = await Promise.all([
          Promise.allSettled(ids.map(id => unifiedGetDoc('watchlists', id))),
          Promise.allSettled(ids.map(id => socialService.getWatchlistLikers(id))),
        ]);
        if (cancelled) return;
        setWatchlistEngagement(prev => {
          const next = new Map(prev);
          docs.forEach((res, i) => {
            const id = ids[i];
            if (res.status !== 'fulfilled' || !res.value?.exists?.()) return;
            const data = res.value.data?.() ?? res.value;
            const posters: string[] = Array.isArray(data?.movieDetails)
              ? data.movieDetails.slice(0, 4)
                  .map((m: any) => m.poster || m.Poster || m.poster_path)
                  .filter(Boolean)
              : [];
            const likedBy = likers[i].status === 'fulfilled' ? (likers[i] as any).value as string[] : [];
            next.set(id, {
              likeCount: data?.followerCount ?? 0,
              likedBy,
              commentCount: data?.commentCount ?? 0,
              name: data?.name || 'Watchlist',
              description: data?.description,
              moviePosters: posters,
              movieCount: data?.movieCount ?? data?.movieDetails?.length ?? 0,
              authorUid: watchlistAuthor.get(id) || data?.createdBy || data?.ownerId || '',
            });
          });
          return next;
        });
        backfillCommentCounts('watchlist', ids, docs, setWatchlistEngagement, () => cancelled);
      }
      if (reviewIds.size > 0) {
        const ids = Array.from(reviewIds);
        const docs = await Promise.allSettled(ids.map(id => unifiedGetDoc('userMovies', id)));
        if (cancelled) return;
        setReviewEngagement(prev => {
          const next = new Map(prev);
          docs.forEach((res, i) => {
            const id = ids[i];
            if (res.status === 'fulfilled' && res.value?.exists?.()) {
              const data: any = res.value.data?.() ?? res.value;
              const likedBy = Array.isArray(data?.reviewLikedBy) ? data.reviewLikedBy : [];
              next.set(id, {
                likeCount: typeof data?.reviewLikes === 'number' ? data.reviewLikes : likedBy.length,
                likedBy,
                commentCount: data?.reviewCommentCount ?? 0,
              });
            } else if (!next.has(id)) {
              next.set(id, { likeCount: 0, likedBy: [], commentCount: 0 });
            }
          });
          return next;
        });
        backfillCommentCounts('review', ids, docs, setReviewEngagement, () => cancelled);
      }
    })();

    return () => { cancelled = true; };
  }, [feedItems]);

  /** Build a SocialPostData for a feed item using the current engagement cache. */
  const socialPostFor = useCallback((item: CrewFeedItem): SocialPostData | null => {
    if (item.kind === 'crew_post' && item.crewPost) {
      return customPostToSocialPost(item);
    }
    if (item.kind === 'movie_watched' || item.kind === 'movie_added') {
      const first = item.activities[0];
      if (!first) return null;
      const isReview = first.type === 'movie_reviewed' || !!first.metadata?.review;
      if (isReview) {
        const reviewId =
          first.reviewId || (first.uid && first.movieId ? `${first.uid}_${first.movieId}` : '');
        if (!reviewId) return null;
        const eng = reviewEngagement.get(reviewId);
        return reviewToSocialPost({
          id: reviewId,
          userId: first.uid,
          movieId: first.movieId,
          movieTitle: first.metadata?.movieTitle,
          moviePoster: first.metadata?.moviePoster,
          movieYear: first.metadata?.movieYear,
          movieGenres: first.metadata?.movieGenre ? [first.metadata.movieGenre] : undefined,
          rating: first.rating,
          review: first.metadata?.review,
          reviewLikes: eng?.likeCount ?? first.metadata?.reviewLikes,
          reviewLikedBy: eng?.likedBy ?? first.metadata?.reviewLikedBy,
          reviewCommentCount: eng?.commentCount ?? first.metadata?.reviewCommentCount,
          updatedDate: first.createdAt,
          watchedDate: (first.metadata as any)?.watchedDate,
          watchedDatePrecision: (first.metadata as any)?.watchedDatePrecision,
        });
      }
      const bid = bundleIdFromFeedItem(item);
      const eng = bid ? bundleEngagement.get(bid) : undefined;
      return bundleToSocialPost(item, eng || { likeCount: 0, likedBy: [], commentCount: 0 });
    }
    if (item.kind === 'watchlist_created') {
      const first = item.activities[0] as any;
      const wid = first?.watchlistId || first?.entityId || first?.listId;
      if (!wid) return null;
      const meta = watchlistEngagement.get(wid);
      return watchlistToSocialPost({
        watchlistId: wid,
        authorUid: item.actorId,
        createdAtMs: item.createdAt,
        name: meta?.name || first?.metadata?.watchlistName || 'Watchlist',
        description: meta?.description ?? first?.metadata?.watchlistDescription,
        moviePosters: meta?.moviePosters,
        movieCount: meta?.movieCount,
        followerCount: meta?.likeCount ?? 0,
        likedBy: meta?.likedBy ?? [],
        commentCount: meta?.commentCount ?? 0,
      });
    }
    return null;
  }, [bundleEngagement, watchlistEngagement, reviewEngagement]);

  const postsByItemId = useMemo(() => {
    const map = new Map<string, SocialPostData>();
    for (const item of feedItems) {
      const p = socialPostFor(item);
      if (p) map.set(item.id, p);
    }
    return map;
  }, [feedItems, socialPostFor]);

  const handleOpenPostDialog = useCallback(async (post: SocialPostData, highlightCommentId?: string) => {
    setPendingHighlightCommentId(highlightCommentId ?? null);
    setSelectedPost(post);
    // For review posts, refresh engagement from source-of-truth before showing dialog
    if (post.kind === 'review') {
      try {
        const doc = await unifiedGetDoc('userMovies', post.id);
        if (doc?.exists?.()) {
          const data: any = doc.data?.() ?? doc;
          const freshLikedBy = Array.isArray(data?.reviewLikedBy) ? data.reviewLikedBy : [];
          const freshEng = {
            likeCount: typeof data?.reviewLikes === 'number' ? data.reviewLikes : freshLikedBy.length,
            likedBy: freshLikedBy,
            commentCount: data?.reviewCommentCount ?? 0,
          };
          setReviewEngagement(prev => { const next = new Map(prev); next.set(post.id, freshEng); return next; });
          setSelectedPost(prev => prev && prev.id === post.id
            ? { ...prev, likedBy: freshLikedBy, likeCount: freshEng.likeCount, commentCount: freshEng.commentCount }
            : prev);
        }
      } catch { /* keep stale data on error */ }
    }
  }, []);

  const handleLikedByClick = useCallback(async (post: SocialPostData) => {
    // For review posts, fetch fresh likedBy from source-of-truth
    if (post.kind === 'review') {
      try {
        const doc = await unifiedGetDoc('userMovies', post.id);
        if (doc?.exists?.()) {
          const data: any = doc.data?.() ?? doc;
          const freshLikedBy = Array.isArray(data?.reviewLikedBy) ? data.reviewLikedBy : [];
          setLikedByUserIds(freshLikedBy);
          return;
        }
      } catch { /* fall through to stale data */ }
    }
    setLikedByUserIds(post.likedBy);
  }, []);

  // Optimistic toggle that updates the right engagement source per kind.
  const handleLikeToggle = useCallback(async (post: SocialPostData) => {
    if (!user?.uid) return;
    const isLiked = post.likedBy.includes(user.uid);
    const action = isLiked ? 'unlike' : 'like';
    const delta = isLiked ? -1 : 1;
    const nextLikedBy = isLiked ? post.likedBy.filter(u => u !== user.uid) : [...post.likedBy, user.uid];

    // Keep the open dialog (if any) in sync so it reflects the new state.
    setSelectedPost(prev => prev && prev.id === post.id && prev.kind === post.kind
      ? { ...prev, likedBy: nextLikedBy, likeCount: Math.max(0, prev.likeCount + delta) }
      : prev);

    try {
      if (post.kind === 'customPost') {
        // Optimistic update on feed item
        setFeedItems(prev => prev.map(fi => {
          if (fi.crewPost?.postDocId !== post.id) return fi;
          const cp = fi.crewPost;
          return {
            ...fi,
            crewPost: {
              ...cp,
              likedBy: nextLikedBy,
              likeCount: Math.max(0, (cp.likeCount ?? 0) + delta),
            },
          };
        }));
        try {
          const result = await toggleCrewPostLike(post.id, user.uid, action);
          if (result?.success) {
            // Fetch actual crew post to sync with server
            const postDoc = await unifiedGetDoc('crewPosts', post.id);
            if (postDoc?.exists?.()) {
              const data = postDoc.data?.() ?? postDoc;
              setFeedItems(prev => prev.map(fi => {
                if (fi.crewPost?.postDocId !== post.id) return fi;
                return {
                  ...fi,
                  crewPost: {
                    ...fi.crewPost,
                    likeCount: data?.likeCount ?? fi.crewPost.likeCount,
                    likedBy: Array.isArray(data?.likedBy) ? data.likedBy : fi.crewPost.likedBy,
                  },
                };
              }));
            }
          }
        } catch (err) {
          console.error('Custom post like toggle failed:', err);
          throw err;
        }
      } else if (post.kind === 'bundle' && post.bundle) {
        setBundleEngagement(prev => {
          const next = new Map(prev);
          const cur = next.get(post.id) || { likeCount: 0, likedBy: [], commentCount: 0 };
          next.set(post.id, {
            ...cur,
            likeCount: Math.max(0, cur.likeCount + delta),
            likedBy: nextLikedBy,
          });
          return next;
        });
        try {
          const result = await toggleBundleLike(post.id, post.authorUid, post.bundle.activityKind, action);
          if (result?.success) {
            // Fetch the actual document from server to sync likeCount and likedBy
            const bundleDoc = await unifiedGetDoc('socialBundles', post.id);
            if (bundleDoc?.exists?.()) {
              const data = bundleDoc.data?.() ?? bundleDoc;
              setBundleEngagement(prev => {
                const next = new Map(prev);
                next.set(post.id, {
                  likeCount: data?.likeCount ?? 0,
                  likedBy: Array.isArray(data?.likedBy) ? data.likedBy : [],
                  commentCount: data?.commentCount ?? 0,
                });
                return next;
              });
            }
          }
        } catch (likeErr) {
          console.error('Bundle like toggle failed:', likeErr);
          throw likeErr;
        }
      } else if (post.kind === 'watchlist') {
        setWatchlistEngagement(prev => {
          const next = new Map(prev);
          const cur = next.get(post.id) || {
            likeCount: 0,
            likedBy: [],
            commentCount: 0,
            name: post.watchlist?.name || 'Watchlist',
            authorUid: post.authorUid,
          };
          next.set(post.id, {
            ...cur,
            likeCount: Math.max(0, cur.likeCount + delta),
            likedBy: nextLikedBy,
          });
          return next;
        });
        try {
          if (isLiked) {
            await socialService.unlikeWatchlist(user.uid, post.id);
          } else {
            await socialService.likeWatchlist(user.uid, post.id);
          }
          // Fetch updated watchlist data to sync likeCount with server
          const watchlistDoc = await unifiedGetDoc('watchlists', post.id);
          if (watchlistDoc?.exists?.()) {
            const data = watchlistDoc.data?.() ?? watchlistDoc;
            const likers = await socialService.getWatchlistLikers(post.id);
            setWatchlistEngagement(prev => {
              const next = new Map(prev);
              const cur = next.get(post.id);
              if (cur) {
                next.set(post.id, {
                  ...cur,
                  likeCount: data?.followerCount ?? 0,
                  likedBy: likers || [],
                });
              }
              return next;
            });
          }
        } catch (err) {
          console.error('Watchlist like toggle failed:', err);
          throw err;
        }
      } else if (post.kind === 'review') {
        // Review like API: movieService.toggleReviewLike. Update activity metadata in feed item.
        setReviewEngagement(prev => {
          const next = new Map(prev);
          const cur = next.get(post.id) || { likeCount: post.likeCount, likedBy: post.likedBy, commentCount: post.commentCount };
          next.set(post.id, {
            ...cur,
            likeCount: Math.max(0, cur.likeCount + delta),
            likedBy: nextLikedBy,
          });
          return next;
        });
        setFeedItems(prev => prev.map(fi => {
          if (fi.kind !== 'movie_watched' || !fi.activities.length) return fi;
          const first = fi.activities[0];
          const fiReviewId = first.reviewId || (first.uid && first.movieId ? `${first.uid}_${first.movieId}` : '');
          if (fiReviewId !== post.id) return fi;
          const md = first.metadata || {};
          const curLikes = (md.reviewLikes as number | undefined) ?? 0;
          return {
            ...fi,
            activities: [
              {
                ...first,
                metadata: {
                  ...md,
                  reviewLikes: Math.max(0, curLikes + delta),
                  reviewLikedBy: nextLikedBy,
                },
              },
              ...fi.activities.slice(1),
            ],
          };
        }));
        const { movieService } = await import('@watchlist/shared');
        await movieService.toggleReviewLike(post.id, user.uid);
        // Re-sync with server truth
        try {
          const reviewDoc = await unifiedGetDoc('userMovies', post.id);
          if (reviewDoc?.exists?.()) {
            const data: any = reviewDoc.data?.() ?? reviewDoc;
            const likedBy = Array.isArray(data?.reviewLikedBy) ? data.reviewLikedBy : [];
            setReviewEngagement(prev => {
              const next = new Map(prev);
              next.set(post.id, {
                likeCount: typeof data?.reviewLikes === 'number' ? data.reviewLikes : likedBy.length,
                likedBy,
                commentCount: data?.reviewCommentCount ?? 0,
              });
              return next;
            });
          }
        } catch (syncErr) {
          console.warn('Review like sync failed:', syncErr);
        }
      }
    } catch (err) {
      console.error('handleLikeToggle error:', err);
      // Reload feed on failure so UI doesn't diverge from server.
      loadFeed(true);
    }
  }, [user?.uid, loadFeed]);

  // Listen for admin-panel deletions so the feed reflects them immediately.
  // Also persist the deleted IDs in module-level sets so items stay gone after
  // tab switches (when CrewPage unmounts and remounts from cache).
  useEffect(() => {
    const onPostDeleted = (e: Event) => {
      const { postDocId } = (e as CustomEvent).detail ?? {};
      if (postDocId) {
        _deletedCrewPostIds.add(postDocId);
        setFeedItems(prev => prev.filter(item => item.crewPost?.postDocId !== postDocId));
      }
    };
    const onBundleDeleted = (e: Event) => {
      const { bundleId } = (e as CustomEvent).detail ?? {};
      if (bundleId) {
        _deletedBundleIds.add(bundleId);
        setFeedItems(prev => prev.filter(item => bundleIdFromFeedItem(item) !== bundleId));
      }
    };
    window.addEventListener('crewPostDeleted', onPostDeleted);
    window.addEventListener('crewBundleDeleted', onBundleDeleted);
    return () => {
      window.removeEventListener('crewPostDeleted', onPostDeleted);
      window.removeEventListener('crewBundleDeleted', onBundleDeleted);
    };
  }, []);

  // You tab has its own loading path — don't block it on isFeedLoading (which is held
  // for the All-tab cold-load sort). Other tabs show skeleton until feed data is ready.
  const showSkeleton = followLoading || (activeTab === TAB_YOU ? isYouLoading : isFeedLoading);
  const followingIds = followStats.followedUsers;
  const followingSet = useMemo(() => new Set(followingIds), [followingIds]);
  const filteredFeedItems = useMemo(() => {
    const nowMs = Date.now();
    // Strip items deleted via admin panel this session (persists across tab switches).
    const hasDeleted = _deletedBundleIds.size > 0 || _deletedCrewPostIds.size > 0 || _deletedReviewIds.size > 0;
    const live = hasDeleted
      ? feedItems.filter(i => {
          if (i.kind === 'crew_post') return !_deletedCrewPostIds.has(i.crewPost?.postDocId ?? '');
          const bid = bundleIdFromFeedItem(i);
          if (bid && _deletedBundleIds.has(bid)) return false;
          if (_deletedReviewIds.size > 0 && i.activities.length > 0) {
            const first = i.activities[0];
            const rid = (first as any)?.reviewId ?? (first?.uid && first?.movieId ? `${first.uid}_${first.movieId}` : '');
            if (rid && _deletedReviewIds.has(rid)) return false;
          }
          return true;
        })
      : feedItems;

    if (activeTab === TAB_YOU) {
      if (!user?.uid) return [];
      // Use dedicated paginated You-tab data (bundled from youActivities)
      // Hidden posts are excluded here — they appear only on the Hidden From Crew page
      return youItems.filter(i => {
        if (_deletedCrewPostIds.has(i.crewPost?.postDocId ?? '')) return false;
        const bid = bundleIdFromFeedItem(i);
        if (bid && _deletedBundleIds.has(bid)) return false;
        if (i.activities.some(a => a.hiddenFromCrew)) return false;
        return true;
      });
    }
    if (activeTab === TAB_FOLLOWING) {
      // Crew posts from the global feed (non-pinned only)
      const crewPostItems = live.filter(i => {
        if (i.kind !== 'crew_post') return false;
        const isActivelyPinned = i.crewPost?.isPinned && (i.crewPost?.pinExpiresAtMs ?? 0) > nowMs;
        return !isActivelyPinned;
      });
      // User activity from paginated following feed
      const followingBundled = bundleCrewItems(followingActivities);
      const followingFiltered = followingBundled.filter(i => {
        const bid = bundleIdFromFeedItem(i);
        if (bid && _deletedBundleIds.has(bid)) return false;
        return true;
      });
      // Merge crew posts + following activity
      const merged = mergeCrewFeedItems(followingFiltered, crewPostItems, nowMs);

      return merged;
    }
    // TAB_ALL: visibility-filtered global feed (non-followed see only reviews + public watchlists).
    // Test-role users are additionally hidden from non-followers entirely.
    return filterFeedItemsForAllTab(live, followingIds, user?.uid, testerUidSet);
  }, [feedItems, youItems, followingActivities, activeTab, user?.uid, followingIds, followingSet, testerUidSet]);
  // Per-tab engagement sort state — keyed by tab index so each tab has its own sorted list.
  const [sortedByTab, setSortedByTab] = useState<Record<number, CrewFeedItem[] | null>>({});
  const engagementSortedItems = activeTab === TAB_YOU ? null : (sortedByTab[activeTab] ?? null);
  const setEngagementSortedItems = useCallback((
    updater: CrewFeedItem[] | null | ((prev: CrewFeedItem[] | null) => CrewFeedItem[] | null)
  ) => {
    setSortedByTab(prev => {
      const current = prev[activeTab] ?? null;
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (next === current) return prev;
      return { ...prev, [activeTab]: next };
    });
  }, [activeTab]);

  // Per-tab sort-applied flag — prevents re-sorting on tab return when data hasn't changed.
  const engagementSortAppliedByTabRef = useRef<Record<number, boolean>>({});
  const engagementSortAppliedRef = { // thin wrapper so existing code reads/writes per-tab
    get current() { return !!engagementSortAppliedByTabRef.current[activeTab]; },
    set current(v: boolean) { engagementSortAppliedByTabRef.current = { ...engagementSortAppliedByTabRef.current, [activeTab]: v }; },
  };

  // After engagement data first arrives, sort once per tab and cache the result.
  // Skipped for the All tab when feedItems was already pre-sorted by loadFeed().
  useEffect(() => {
    if (activeTab === TAB_YOU) { setEngagementSortedItems(null); return; }
    if (bundleEngagement.size === 0) { setEngagementSortedItems(null); return; }

    // Warm load (All tab only): feedItems already sorted by loadFeed — no sort needed.
    if (activeTab === TAB_ALL && feedItemsPreSortedRef.current && !engagementSortAppliedRef.current) {
      engagementSortAppliedRef.current = true;
      setEngagementSortedItems(null);
      return;
    }

    if (engagementSortAppliedRef.current) {
      // Sort already applied for this tab's data set — only append new items from pagination.
      setEngagementSortedItems(prev => {
        if (!prev) return null;
        const existingIds = new Set(prev.map(i => i.id));
        const newItems = filteredFeedItems.filter(i => !existingIds.has(i.id));
        return newItems.length > 0 ? [...prev, ...newItems] : prev;
      });
      return;
    }

    // First sort for this tab.
    const nowMs = Date.now();
    const sorted = applyFeedOrder(filteredFeedItems, nowMs, bundleEngagement);
    setEngagementSortedItems(sorted);
    engagementSortAppliedRef.current = true;
    // Cold-load All-tab path: skeleton was held — reveal the feed now.
    if (activeTab === TAB_ALL) setIsFeedLoading(false);
  }, [filteredFeedItems, bundleEngagement, activeTab]);

  // Use engagement-sorted items once available, otherwise fall back to chronological
  const displayItems = engagementSortedItems ?? filteredFeedItems;
  const visibleFeedItems = displayItems.slice(0, visibleCount);
  const hasMore = displayItems.length > visibleCount;


  const showFindCrew =
    activeTab === TAB_ALL && !hideFindCrew && visibleSuggestedUsers.length > 0;


  // Deep link from notifications: /crew?postId=<crewPosts doc id>[&openComments=true]
  useEffect(() => {
    highlightScrollDoneRef.current = false;
    openCommentsDoneRef.current = false;
  }, [highlightPostId]);

  // Deep link from notifications: /crew?bundleId=<actorUid_kind_YYYYMMDD>[&openComments=true]
  useEffect(() => {
    bundleScrollDoneRef.current = false;
  }, [highlightBundleId]);

  useEffect(() => {
    if (!highlightPostId || highlightScrollDoneRef.current || showSkeleton || displayItems.length === 0) {
      return;
    }
    const targetFeedId = `crewpost_${highlightPostId}`;
    let idx = displayItems.findIndex(i => i.id === targetFeedId);
    if (idx < 0) {
      // Post not visible in current tab — switch to All tab where it will be found
      if (activeTab !== TAB_ALL) {
        handleTabChange(TAB_ALL);
        return;
      }
      highlightScrollDoneRef.current = true;
      return;
    }
    if (idx >= visibleCount) {
      setVisibleCount(v => Math.min(Math.max(v, idx + 1), displayItems.length));
      return;
    }
    highlightScrollDoneRef.current = true;
    const doScroll = () => {
      const el = document.getElementById(`crew-feed-${targetFeedId}`);
      if (!el) {
        // Element not in DOM yet — retry after next paint
        setTimeout(doScroll, 80);
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (openCommentsOnArrival && !openCommentsDoneRef.current) {
        openCommentsDoneRef.current = true;
        const feedItem = displayItems[idx];
        const post = socialPostFor(feedItem);
        if (post) {
          setTimeout(() => handleOpenPostDialog(post), 400);
        }
      }
    };
    requestAnimationFrame(doScroll);
  }, [highlightPostId, openCommentsOnArrival, displayItems, visibleCount, showSkeleton, activeTab, handleTabChange, socialPostFor, handleOpenPostDialog]);

  useEffect(() => {
    if (!highlightBundleId || bundleScrollDoneRef.current || showSkeleton || displayItems.length === 0) {
      return;
    }
    let idx = displayItems.findIndex(i => bundleIdFromFeedItem(i) === highlightBundleId);
    if (idx < 0) {
      // Bundle not visible in current tab — switch to All tab where it will be found
      if (activeTab !== TAB_ALL) {
        handleTabChange(TAB_ALL);
        return;
      }
      bundleScrollDoneRef.current = true;
      return;
    }
    if (idx >= visibleCount) {
      setVisibleCount(v => Math.min(Math.max(v, idx + 1), displayItems.length));
      return;
    }
    bundleScrollDoneRef.current = true;
    const targetId = displayItems[idx].id;
    const doScroll = () => {
      const el = document.getElementById(`crew-feed-${targetId}`);
      if (!el) {
        setTimeout(doScroll, 80);
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (openCommentsOnArrival && !openCommentsDoneRef.current) {
        openCommentsDoneRef.current = true;
        const post = socialPostFor(displayItems[idx]);
        if (post) {
          setTimeout(() => handleOpenPostDialog(post), 400);
        }
      }
    };
    requestAnimationFrame(doScroll);
  }, [highlightBundleId, openCommentsOnArrival, displayItems, visibleCount, showSkeleton, activeTab, handleTabChange, socialPostFor, handleOpenPostDialog]);

  // Shared scope-tabs row — used both inline (below stories) and inside the AppBar when scrolled.
  const hiddenPostCount = youItems.filter(i => i.activities.some(a => a.hiddenFromCrew)).length;
  const renderTabsRow = (size: 'default' | 'large', compressible = false) => {
    const iconSize = size === 'large' ? 18 : 16;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, ...(compressible ? {} : { width: 'fit-content' }) }}>
        <CustomTabs
          value={activeTab}
          onChange={handleTabChange}
          onReselect={handleTabReselect}
          size={size}
          compressible={compressible}
          tabs={[
            { label: 'Hot', value: TAB_ALL, icon: <FlameIcon size={iconSize} weight={activeTab === TAB_ALL ? 'duotone' : 'regular'} /> },
            { label: 'Following', value: TAB_FOLLOWING, icon: <UsersIcon size={iconSize} weight={activeTab === TAB_FOLLOWING ? 'duotone' : 'regular'} /> },
            { label: 'You', value: TAB_YOU, icon: <UserIcon size={iconSize} weight={activeTab === TAB_YOU ? 'duotone' : 'regular'} /> },
          ]}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {/* AppBar — mirrors HomePage shell for visual continuity (no jerk when switching tabs) */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        bgcolor: isScrolled
          ? (isDark ? 'rgba(28, 28, 30, 0.6)' : theme.palette.background.default)
          : 'transparent',
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${isScrolled ? (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)') : 'transparent'}`,
        pt: { xs: 'calc(var(--safe-area-inset-top) + 8px)', sm: 'calc(var(--safe-area-inset-top) + 10px)', md: 'calc(var(--safe-area-inset-top) + 12px)' },
        pb: { xs: 1, sm: 1.25, md: 1.5 },
        transition: 'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        width: '100%',
      }}>
        <ResponsiveContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            {/* Left slot: Logo by default; once the inline tabs scroll out of view
                it swaps to the scope tabs (+ 3-dot menu) so they stay reachable. */}
            <Box sx={{
              flex: 1,
              minWidth: 0,
              height: 56,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
            }}>
              {tabsInAppBar ? (
                renderTabsRow('default', true)
              ) : (

                // Compensate for the transparent left padding baked into the logo SVG
                // so the visible mark aligns with the tabs that replace it on scroll.
                <Box sx={{ ml: -1, display: 'flex', alignItems: 'center' }}>
                  <Logo
                    size="large"
                    variant="default"
                    state="slow-animated"
                  />
                </Box>
              )}
            </Box>

            {/* Overflow menu — left of notifications */}
            <IconButton
              onClick={handleAppBarMenuOpen}
              size="small"
              aria-label="More options"
              sx={{
                color: theme.palette.text.primary,
                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                flexShrink: 0,
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                },
              }}
            >
              <DotsThreeIcon size={22}  weight="bold" />
            </IconButton>

            {/* Opening Credits — desktop only, visible beside the 3-dot button */}
            <IconButton
              onClick={() => setOpeningCreditsOpen(true)}
              size="small"
              aria-label="Help"
              sx={{
                display: { xs: 'none', md: 'flex' },
                color: theme.palette.text.primary,
                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                flexShrink: 0,
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)' },
              }}
            >
              <HelpOutline sx={{ fontSize: 22 }} />
            </IconButton>

            {/* Notifications bell — always on the right */}
            <IconButton
              onClick={() => navigate('/notifications')}
              size="small"
              sx={{
                color: theme.palette.text.primary,
                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                flexShrink: 0,
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                },
              }}
            >
              <Badge
                badgeContent={unreadCount > 99 ? '99+' : unreadCount}
                color="error"
                invisible={unreadCount === 0}
                sx={{ '& .MuiBadge-badge': { fontSize: 10, fontWeight: 600, height: 18, minWidth: 18, padding: '0 4px' } }}
              >
                {unreadCount > 0
                  ? <BellIcon size={22} weight="fill" />
                  : <BellSimpleIcon size={22} />}
              </Badge>
            </IconButton>
          </Box>
        </ResponsiveContainer>

        {/* Overflow menu (Hidden from Crew / hide Find Crew suggestions) */}
        <Menu
          anchorEl={appBarMenuAnchor}
          open={appBarMenuOpen}
          onClose={handleAppBarMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { ...glassStyles.menu(isDark), minWidth: 220 } }}
        >
          <MenuItem
            onClick={(e) => { e.preventDefault(); toggleHideFindCrew(); }}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemIcon>
                <PersonAddAlt1 fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary="Friend suggestions"
                primaryTypographyProps={{
                  fontFamily: font(),
                  fontSize: 14,
                  color: theme.palette.text.primary,
                }}
              />
            </Box>
            <Switch
              size="small"
              edge="end"
              checked={!hideFindCrew}
              onChange={() => toggleHideFindCrew()}
              onClick={(e) => e.stopPropagation()}
            />
          </MenuItem>
          <MenuItem onClick={handleHiddenFromCrewNavigate}>
            <ListItemIcon>
              <VisibilityOff fontSize="small" sx={{ color: theme.palette.text.secondary }} />
            </ListItemIcon>
            <ListItemText
              primary="Hidden from Crew"
              primaryTypographyProps={{
                fontFamily: font(),
                fontSize: 14,
                color: theme.palette.text.primary,
              }}
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleAppBarMenuClose();
              setOpeningCreditsOpen(true);
            }}
          >
            <ListItemIcon>
              <HelpOutline fontSize="small" sx={{ color: theme.palette.text.secondary }} />
            </ListItemIcon>
            <ListItemText
              primary="Help"
              primaryTypographyProps={{
                fontFamily: font(),
                fontSize: 14,
                color: theme.palette.text.primary,
              }}
            />
          </MenuItem>
          <MenuItem onClick={() => { handleAppBarMenuClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <GearIcon size={18} color={theme.palette.text.secondary} />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              primaryTypographyProps={{
                fontFamily: font(),
                fontSize: 14,
                color: theme.palette.text.primary,
              }}
            />
          </MenuItem>
        </Menu>
      </Box>

      <PullToRefresh onRefresh={handleRefresh}>
        <ResponsiveContainer>
          <StoriesRail refreshKey={storiesRefreshKey} />
        </ResponsiveContainer>

        {/* Onboarding + feature-announcement card stack — Phase D */}
        <ResponsiveContainer sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <HomeCardStack />
        </ResponsiveContainer>

        {/* Inline scope tabs — hidden when the AppBar is already mirroring them. */}
        <Box ref={inlineTabsRef} sx={{ visibility: tabsInAppBar ? 'hidden' : 'visible' }}>
          <ResponsiveContainer>{renderTabsRow('large')}</ResponsiveContainer>
        </Box>

        <ResponsiveContainer sx={{ pt: { xs: 4.5, sm: 5 }, pb: { xs: 1, sm: 2 } }}>
          {(showSkeleton || isSwitchingTab) && (
            <Box>{[0, 1, 2, 3].map(i => <CrewFeedItemSkeleton key={i} />)}</Box>
          )}

          {!showSkeleton && error && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ fontFamily: font(), color: theme.palette.text.secondary, fontSize: 14 }}>
                {error}
              </Typography>
            </Box>
          )}

          {!showSkeleton && !isSwitchingTab && !error && filteredFeedItems.length === 0 && !isYouLoading && (
            <EmptyState
              icon={<Groups sx={{ fontSize: 56, color: theme.palette.text.disabled }} />}
              heading={
                activeTab === TAB_YOU
                  ? 'Nothing from you yet'
                  : activeTab === TAB_FOLLOWING
                  ? (followingIds.length === 0 ? 'Your crew feed is quiet' : 'Nothing new from your crew')
                  : 'No posts yet'
              }
              body={
                activeTab === TAB_YOU
                  ? 'Your watches, ratings, lists, and posts will show up here.'
                  : activeTab === TAB_FOLLOWING
                  ? (followingIds.length === 0
                    ? 'Follow people for their watches, ratings, and lists. Official posts from Watchlist Crew also appear here when published.'
                    : "When people you follow watch or rate films, or create new lists, it'll show up here.")
                  : 'Reviews and new public watchlists from across Watchlist will show up here.'
              }
            />
          )}

          {!showSkeleton && !isSwitchingTab && activeTab === TAB_YOU && (
            <Box
              onClick={handleHiddenFromCrewNavigate}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mx: 0,
                mb: 3,
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                borderRadius: 2,
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                '&:active': { opacity: 0.7 },
              }}
            >
              <VisibilityOff fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                {hiddenPostCount} hidden post{hiddenPostCount !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
          {!showSkeleton && !isSwitchingTab && filteredFeedItems.length > 0 && (
            <Box>
              {visibleFeedItems.map((item, idx) => {
                const showInlineFollow =
                  activeTab === TAB_ALL &&
                  item.kind !== 'crew_post' &&
                  !!user?.uid &&
                  item.actorId !== user.uid &&
                  !followingSet.has(item.actorId);
                return (
                <React.Fragment key={item.id}>
                  <Box id={`crew-feed-${item.id}`}>
                    <CrewFeedItemComponent
                      item={item}
                      post={postsByItemId.get(item.id)}
                      actorName={getUserDisplayName(item.actorId, 'User') || 'User'}
                      actorAvatar={getUserAvatar(item.actorId) || undefined}
                      currentUser={user}
                      onMovieSelect={handleMovieSelect}
                      onActorSelect={handleActorSelect}
                      onWatchlistSelect={handleWatchlistSelect}
                      onReviewSelect={handleReviewSelect}
                      onPersonSelect={handlePersonSelect}
                      onHideFromCrew={handleHideFromCrew}
                      onDeleteCrewPost={canModerateCrew ? handleDeleteCrewPost : undefined}
                      onAdminDelete={canModerateCrew ? handleAdminDelete : undefined}
                      onLikeToggle={handleLikeToggle}
                      onOpenPostDialog={handleOpenPostDialog}
                      onLikedByClick={handleLikedByClick}
                      headerAccessory={
                        showInlineFollow ? (
                          <InlineFollowButton
                            userId={item.actorId}
                            onFollow={followUser}
                            onSuccess={() => {
                              const name = getUserDisplayName(item.actorId, 'User') || 'User';
                              showSuccess(
                                `Following ${name}`,
                                undefined,
                                { label: 'Undo', onClick: () => unfollowUser(item.actorId) },
                              );
                            }}
                          />
                        ) : undefined
                      }
                    />
                  </Box>
                  {showFindCrew && idx === 2 && (
                    <Box sx={{ my: 1 }}>
                      <SuggestedUsersCarousel
                        compact
                        suggestions={visibleSuggestedUsers}
                        currentUserId={user?.uid}
                        isUserFollowed={isUserFollowed}
                        onUserClick={handleActorSelect}
                        onFollowToggle={handleFollowToggle}
                        onDismissCard={handleDismissSuggestionCard}
                        onViewAll={handleFindCrewNavigate}
                      />
                      <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', mb: 1 }} />
                    </Box>
                  )}
                </React.Fragment>
                );
              })}
              {hasMore && (
                <LoadMoreSentinel onVisible={() => setVisibleCount(v => v + PAGE_SIZE)} />
              )}
              {!hasMore && isFullFeedLoading && activeTab === TAB_ALL && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 3 }}>
                  <CircularProgress size={16} sx={{ color: theme.palette.text.disabled }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                    Loading more posts…
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </ResponsiveContainer>
      </PullToRefresh>

      <SocialPostDialog
        open={Boolean(selectedPost)}
        onClose={() => { setSelectedPost(null); setPendingHighlightCommentId(null); }}
        post={selectedPost}
        currentUser={user}
        authorName={selectedPost ? getUserDisplayName(selectedPost.authorUid, 'User') || 'User' : undefined}
        authorAvatar={selectedPost ? getUserAvatar(selectedPost.authorUid) || undefined : undefined}
        onLikeToggle={selectedPost ? () => handleLikeToggle(selectedPost) : undefined}
        onLikedByClick={selectedPost ? () => handleLikedByClick(selectedPost) : undefined}
        onUserClick={(uid) => { setSelectedPost(null); handleActorSelect(uid); }}
        onMovieSelect={(id) => { setSelectedPost(null); handleMovieSelect(id); }}
        onWatchlistSelect={(id) => { setSelectedPost(null); handleWatchlistSelect(id); }}
        onReviewSelect={(id) => { setSelectedPost(null); handleReviewSelect(id); }}
        onPersonSelect={(id) => { setSelectedPost(null); handlePersonSelect(id); }}
        disableWebHistoryBack
        highlightCommentId={pendingHighlightCommentId ?? highlightCommentId}
      />

      <LikedByBottomSheet
        open={likedByUserIds.length > 0}
        onClose={() => setLikedByUserIds([])}
        userIds={likedByUserIds}
        onUserClick={(uid) => { setLikedByUserIds([]); handleActorSelect(uid); }}
      />

      <Dialog open={!!pendingDeletePostId} onClose={() => setPendingDeletePostId(null)}>
        <DialogTitle>Delete Post?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the post. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeletePostId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteCrewPostConfirmed}>Delete</Button>
        </DialogActions>
      </Dialog>

      <OpeningCreditsSheet
        open={openingCreditsOpen}
        onClose={() => setOpeningCreditsOpen(false)}
      />
    </Box>
  );
};

export default CrewPage;

// ─── Inline Follow button (All-posts tab, non-followed authors) ────────────
interface InlineFollowButtonProps {
  userId: string;
  onFollow: (userId: string) => Promise<void> | void;
  onSuccess?: () => void;
}
const InlineFollowButton: React.FC<InlineFollowButtonProps> = ({ userId, onFollow, onSuccess }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [busy, setBusy] = useState(false);
  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await onFollow(userId);
      onSuccess?.();
    } catch (err) {
      console.warn('InlineFollowButton: follow failed', err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={handleClick}
      disabled={busy}
      size="small"
      variant="outlined"
      sx={{
        fontFamily: font(),
        textTransform: 'none',
        fontSize: 12,
        fontWeight: 600,
        minWidth: busy ? 64 : 0,
        width: busy ? 64 : undefined,
        px: 1.25,
        py: 0.25,
        lineHeight: 1.4,
        borderRadius: 999,
        borderColor: isDark ? 'rgba(255,255,255,0.24)' : 'rgba(0,0,0,0.24)',
        color: theme.palette.text.primary,
        flexShrink: 0,
        alignSelf: 'flex-start',
        '&:hover': {
          borderColor: theme.palette.text.primary,
          bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        },
        '&.Mui-disabled': {
          borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        },
      }}
    >
      {busy
        ? <CircularProgress size={12} sx={{ color: theme.palette.text.disabled }} />
        : 'Follow'}
    </Button>
  );
};

// ─── Empty state helper ────────────────────────────────────────────────────
interface EmptyStateProps {
  icon: React.ReactNode;
  heading: string;
  body: string;
}
const EmptyState: React.FC<EmptyStateProps> = ({ icon, heading, body }) => {
  const theme = useTheme();
  return (
    <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
      {icon}
      <Typography sx={{ fontFamily: font(), fontWeight: 700, fontSize: 16, mt: 2, mb: 1, color: theme.palette.text.primary }}>
        {heading}
      </Typography>
      <Typography sx={{ fontFamily: font(), fontSize: 13, color: theme.palette.text.secondary, maxWidth: 280, mx: 'auto' }}>
        {body}
      </Typography>
    </Box>
  );
};
